import type { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'
import type { ConductorTask } from 'Automations/nodeDefinitions/conductor'
import type {
  Condition,
  ConditionalProperties,
  ConditionSupportingTask
} from 'Automations/nodeDefinitions/conditions'
import { mergeSupportingBranches } from '../mergeSupportingBranches'
import type { SwitchGroup } from '../../switch.types'

export type CollectedSupportingTasks = {
  /**
   * The branches to run before the condition node. Each is ordered: a task may
   * read the output of one before it in the same branch.
   */
  branches: ConductorTask[][]
  /** Runtime key -> task output path, merged into the node's inputs. */
  semanticTypeMapping: Record<string, string>
}

/**
 * Collects the supporting branches every configured condition in a switch
 * depends on, and the runtime keys their outputs are published under.
 *
 * Branches sharing a task are merged, so N rows needing the same data cost one
 * fetch — and so the definition can't carry the duplicate reference names
 * Conductor rejects. Encounter order (group order, then condition order) is
 * preserved to keep generated definitions stable across saves.
 */
export const collectSupportingTasks = ({
  nodeId,
  groups,
  conditionalProperties,
  inputMapping,
  nodes
}: {
  nodeId: string
  groups: SwitchGroup[]
  conditionalProperties: ConditionalProperties
  inputMapping: Record<string, string>
  nodes: AutomationsPresentationV1Node[]
}): CollectedSupportingTasks => {
  const results = groups
    .flatMap(group =>
      group.conditions.map(condition =>
        buildSupportingTask({
          nodeId,
          condition,
          siblingConditions: group.conditions.filter(
            sibling => sibling.id !== condition.id
          ),
          conditionalProperties,
          inputMapping,
          nodes
        })
      )
    )
    .filter((result): result is ConditionSupportingTask => result !== undefined)

  return {
    branches: mergeSupportingBranches(results.map(result => result.tasks)),
    // Duplicate keys always carry an identical path — both the key and the task
    // reference name derive from the same subject — so merge order is moot.
    semanticTypeMapping: results.reduce<Record<string, string>>(
      (acc, result) => ({ ...acc, ...result.semanticTypeMapping }),
      {}
    )
  }
}

const buildSupportingTask = ({
  nodeId,
  condition,
  siblingConditions,
  conditionalProperties,
  inputMapping,
  nodes
}: {
  nodeId: string
  condition: Condition
  siblingConditions: Array<Condition>
  conditionalProperties: ConditionalProperties
  inputMapping: Record<string, string>
  nodes: AutomationsPresentationV1Node[]
}): ConditionSupportingTask | undefined => {
  const property = conditionalProperties[condition.property]
  if (!property?.valueSupportingTask) return undefined

  return property.valueSupportingTask({
    conditionNodeId: nodeId,
    conditionId: condition.id,
    operator: condition.operator,
    siblingConditions,
    value: condition.value,
    inputMapping,
    nodes
  })
}
